import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { AiChatLayout } from '@panaya/ai-chat/shared';
import { AiChatThreadService } from './conversation-tools/ai-chat-thread.service';
import { CanvasFrameStore } from './canvas/canvas-frame.store';

/** Layouts the user picks, remembered between sessions; `canvas` is never stored. */
const STORED_LAYOUTS: AiChatLayout[] = ['floating', 'sidebar'];

@Injectable({ providedIn: 'root' })
export class AiChatLayoutService {
    private readonly STORAGE_KEY = 'ai-chat-layout';
    private readonly _layout$ = new BehaviorSubject<AiChatLayout>(this.readStoredLayout());
    private readonly threadService = inject(AiChatThreadService);
    private readonly frameStore = inject(CanvasFrameStore);
    private readonly destroyRef = inject(DestroyRef);
    /** The layout the user had before the canvas opened; restored on close. */
    private layoutBeforeCanvas: AiChatLayout | null = null;

    readonly layout$ = this._layout$.pipe(distinctUntilChanged());

    /** The splitter panel shows the chat: sidebar or canvas, with an active thread. */
    readonly isPanelOpen$ = combineLatest([this.layout$, this.threadService.isActive]).pipe(
        map(([layout, active]) => (layout === 'sidebar' || layout === 'canvas') && !!active),
        distinctUntilChanged()
    );

    readonly isSidebarOpen$ = combineLatest([this.layout$, this.threadService.isActive]).pipe(
        map(([layout, active]) => layout === 'sidebar' && !!active),
        distinctUntilChanged()
    );

    /** A host binds this to hide its page while a canvas is open. */
    readonly isCanvasOpen$ = combineLatest([this.layout$, this.threadService.isActive]).pipe(
        map(([layout, active]) => layout === 'canvas' && !!active),
        distinctUntilChanged()
    );

    constructor() {
        this.frameStore.isOpen$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isOpen) => (isOpen ? this.enterCanvas() : this.leaveCanvas()));
    }

    get currentLayout(): AiChatLayout {
        return this._layout$.value;
    }

    setLayout(layout: AiChatLayout): void {
        // Canvas is reachable only while a canvas is open.
        if (layout === 'canvas' && !this.frameStore.isOpen()) {
            return;
        }
        this._layout$.next(layout);
        if (STORED_LAYOUTS.includes(layout)) {
            localStorage.setItem(this.STORAGE_KEY, layout);
            if (this.layoutBeforeCanvas !== null) {
                // The user left canvas mode by hand: that choice is what we restore later.
                this.layoutBeforeCanvas = layout;
            }
        }
    }

    private enterCanvas(): void {
        if (this._layout$.value === 'canvas') {
            return;
        }
        this.layoutBeforeCanvas = this._layout$.value;
        this._layout$.next('canvas');
    }

    private leaveCanvas(): void {
        const restore = this.layoutBeforeCanvas;
        this.layoutBeforeCanvas = null;
        if (this._layout$.value === 'canvas') {
            this._layout$.next(restore ?? this.readStoredLayout());
        }
    }

    private readStoredLayout(): AiChatLayout {
        const stored = localStorage.getItem(this.STORAGE_KEY) as AiChatLayout;
        return STORED_LAYOUTS.includes(stored) ? stored : 'floating';
    }
}
